import React, { useEffect, useState, useMemo, ReactNode } from 'react';
import { QuantityPicker } from 'components';
import { Project } from 'au-js-sdk/lib/models/Project';
import {
  CartItemContainer,
  CartItemContent,
  MobileImageQtyPickerGrouping,
  CartItemDetails,
  ButtonList,
  DesktopQtyPriceLinksGrouping,
  CartItemImageDetails,
  DesktopQtyPrice
} from '../styled';
import { fromRange } from '../../../helpers/arrays';
import { getTieredPricing } from 'services/magento/catalog';
import { LoadingState, isFetching, isInitialized } from 'store/constants';
import { useWindowSize, DeviceSize } from 'hooks/useWindowSize';
import { Show } from 'components/Functional';
import { isEqual } from 'helpers/conditionals';
import { newrelic } from 'helpers/reporting/newrelic';
import {
  StyledContent,
  StyledCheck,
  StyledMessage,
  StyledNotificationChip,
  NotificationChipWrap
} from 'components/CartItemList/CartImage/styled';
import styled from 'styled-components';
import useProjects, { CompactProject } from 'hooks/useProjects';
import { HydratedProjectV1 } from 'components/types/projects';
import { CartItemPrice } from './CartItemPrice';
import { CartItemPreview, CartItemPreviewProps } from 'components/CartItemList/CartItem/CartItemPreview';
import { SKU_BRASS_WOOD_DISPLAY_BOX } from 'components/constants/products';
import { useProductSelectors } from 'hooks/useProductSelectors';
import { AUQuoteDataCartItem } from 'types/magento';
import { Product } from 'types/Product';
import { optionGet } from 'faunctions/lib/functions';
import { SPLIT_IO_FEATURE_FLAG_PAGE_PRICING, SPLIT_IO_FEATURE_FLAG_PAGE_PRICING_COOKIE } from 'constants/split';
import { useSplitTreatment } from 'hooks/splits/useSplitTreatment';
import { getProductOptions, getCartItemOptionPriceDetails, getHammerCategory } from '../helpers';
import { OptionDetails } from 'components/DetailsBlock';
import { CartItemOptionDetails } from './CartItemOptionDetails';
import { Option } from 'types/Option';
import { getBasePageCount, isPrintSetWithPerPrintPricing } from 'features/pdp';
import { QuantityPickerLabel } from 'components/QuantityPicker/styled';
import { GIFT_CARD_SKU } from 'constants/orders';
import { useCartItemActions } from 'hooks/UseCartItemActions';

const toOption = (x: number | string) => ({
  label: x.toString(),
  value: x.toString()
});

const optionsFromRange = (range: number[]) => range.map(toOption);

interface CartItemProps {
  item: AUQuoteDataCartItem;
  cartItemProduct: Product;
  handleQtyChange: (value: Option) => void;
  isCartItemFetching: boolean;
  flashId: string;
  flashToken: string;
  email: string;
  customerId: string;
  buttons: JSX.Element[];
  productOptionPriceTransparencySplit?: boolean;
}

interface TieredPricingState {
  loadingState: LoadingState;
  data: any[];
}

const buildJsonAttributes = (stringAttributes) => {
  const productAttributes = {};
  stringAttributes.forEach((prop) => {
    let propName = prop.option_title || prop.name;
    propName = propName.replace(/ /g, '_').toLowerCase();

    let propValue = prop.option_value_title || prop.value;
    propValue = propValue.toLowerCase().replace(/ /g, '').replace(/"/g, '');
    productAttributes[propName] = propValue;
  });

  return productAttributes;
};

const getQuantityOptionsFromState = (state: TieredPricingState) => (qty: number) => {
  const { loadingState, data } = state;
  if (isFetching(loadingState) || isInitialized(loadingState)) {
    return [toOption(qty)];
  }

  return data.length ? data : optionsFromRange(fromRange(1, 999));
};

const quantityTierToInt = (tierLabel: string) => {
  if (!tierLabel.includes || !tierLabel.includes('(')) {
    newrelic('addPageAction')('invalidQuantityTier', {
      tierLabel: tierLabel
    });
    return NaN;
  }
  const parts = tierLabel.split(' ');
  const qty = parseInt(parts[0], 10);
  if (isNaN(qty)) {
    newrelic('addPageAction')('quantityParseFailure', {
      tierLabel: tierLabel
    });
  }
  return qty;
};

export const formatQuantityAsNumber = (qty: string | number): number => {
  const val = typeof qty === 'string' ? parseInt(qty, 10) : qty;
  if (isNaN(val)) {
    return quantityTierToInt(qty as string);
  }

  return val;
};

const MobileMessageChip = styled.div`
  padding-left: 1rem;
  display: inline-block;
  p {
    margin: 0;
    font-size: 0.8rem;
  }
`;

export const useTieredPricingState = (): [
  TieredPricingState,
  React.Dispatch<React.SetStateAction<TieredPricingState>>
] =>
  useState({
    loadingState: LoadingState.INITIALIZED,
    data: []
  });

const CartItem = ({
  item,
  cartItemProduct,
  isCartItemFetching,
  handleQtyChange,
  flashId,
  flashToken,
  email,
  customerId,
  buttons,
  productOptionPriceTransparencySplit
}: CartItemProps): JSX.Element => {
  const { qty, name, price, sku } = item;

  const hammerCategory = getHammerCategory(item);
  const isEditable = item.extension_attributes.is_editable;
  const imgUrl = item.extension_attributes.product_image;
  const livePreviewAttributes = item.extension_attributes.live_preview_attributes;
  const giftWrap = item.extension_attributes.gift_wrapping;
  const giftMessage = item.extension_attributes.gift_message && item.extension_attributes.gift_message.gift_message_id;
  const isGiftCard = sku === GIFT_CARD_SKU;

  const { getCanonicalUrl } = useProductSelectors();
  const canonicalUrl = getCanonicalUrl(sku);

  const [tieredPricing, setTieredPricing] = useTieredPricingState();
  const [project, setProject] = useState<CompactProject | null>(null);
  const [editorUrl, setEditorUrl] = useState('');
  const [showPriceDetails, setShowPriceDetails] = useState<boolean>(false);

  const {
    fetchProject,
    isEditable: shouldRenderEditButton,
    getEditorUrl,
    mapProjectToSmallCompactVersion,
    renameProjectHandler: _renameProjectHandler,
    getHasPrints
  } = useProjects(flashId, flashToken, undefined, false);

  const { deviceSize } = useWindowSize();

  const projectId = optionGet('extension_attributes.au_project_id')(item).getOrElse('');
  const schemaVersion: '1' | '2' = item.extension_attributes?.au_project_version || null;
  const productAttributes = buildJsonAttributes(optionGet('product_option.extension_attributes.readable')(item).value);
  const baseDiscountAmount = optionGet('base_discount_amount')(item).getOrElse('');
  const isBrassWoodDisplayBoxWithPrints = SKU_BRASS_WOOD_DISPLAY_BOX === sku && getHasPrints(project);

  const { treatmentStatus: showVariantPagePricing } = useSplitTreatment(
    SPLIT_IO_FEATURE_FLAG_PAGE_PRICING,
    SPLIT_IO_FEATURE_FLAG_PAGE_PRICING_COOKIE
  );

  const cartItemOptionPriceDetails = useMemo(
    () =>
      getCartItemOptionPriceDetails(
        project,
        cartItemProduct,
        item,
        isBrassWoodDisplayBoxWithPrints,
        showVariantPagePricing
      ),
    [cartItemProduct, item, project, isBrassWoodDisplayBoxWithPrints, showVariantPagePricing]
  );

  const showCustomOptionPrices = productOptionPriceTransparencySplit && item.reporting_product_category !== 'cards';
  const isDeviceSize = isEqual(deviceSize);
  const content: ReactNode = (
    <StyledContent as="span">
      <StyledCheck as="span" />
      <StyledMessage as="span">{`
            ${giftWrap && !giftMessage ? 'Gift Wrap' : ''}
            ${giftWrap && giftMessage ? 'Gift Wrap & Message' : ''}
            ${!giftWrap && giftMessage ? 'Gift Message' : ''}
          `}</StyledMessage>
    </StyledContent>
  );

  const productOptions: OptionDetails[] = useMemo(
    () => getProductOptions(project, cartItemProduct, item, showCustomOptionPrices, showVariantPagePricing),
    [cartItemProduct, item, project, showCustomOptionPrices, showVariantPagePricing]
  );

  useEffect(() => {
    getTieredPricing(sku).then((x) => {
      x.map((y: any[]) => {
        const tiers = y.map((tier) => ({
          // Sending the label `10 ($1.60 per card)` saves complexity on the client
          // Given that this is only required because of legacy pricing structures
          // This is an intentional tradeoff that will be removed.
          value: tier.option_id ? tier.label : tier.quantity,
          label: tier.quantity
        }));
        setTieredPricing({
          loadingState: LoadingState.SUCCESS,
          data: tiers
        });
        return true;
      }).getOrElseL(() => {
        setTieredPricing({
          loadingState: LoadingState.FAILURE,
          data: []
        });
        return false;
      });
    });
  }, [setTieredPricing, sku]);

  useEffect(() => {
    const fetchProjectData = async () => {
      const project = (await fetchProject(projectId, schemaVersion)) as Project | HydratedProjectV1;
      setProject(mapProjectToSmallCompactVersion(project));
    };

    if (projectId) {
      void fetchProjectData();
    }

    // TODO: Refactor this dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, schemaVersion]);

  useEffect(() => {
    if (project) {
      const getEditorUrlResponse = async (project: CompactProject) => {
        const editorUrl = await getEditorUrl(project, email, customerId);
        setEditorUrl(editorUrl);
      };

      void getEditorUrlResponse(project);
    }
    // TODO: Refactor this dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, email, customerId]);

  const actionsList = useCartItemActions({
    buttons,
    productOptionPriceTransparencySplit,
    isEditable,
    shouldRenderEditButton,
    isBrassWoodDisplayBoxWithPrints,
    showCustomOptionPrices,
    cartItemOptionPriceDetails,
    showPriceDetails,
    setShowPriceDetails,
    item,
    email,
    project,
    editorUrl,
    isDeviceSmall: isDeviceSize(DeviceSize.SMALL)
  });

  const renameProjectHandler = async (newName: string) => {
    await _renameProjectHandler(project)(newName);
    fetchProject(projectId);
  };

  const [pdpUrl, setPdpUrl] = useState('');

  useEffect(() => {
    const buildPdpUrl = `${process.env.GATSBY_SITE_URL}/${canonicalUrl}`;
    setPdpUrl(buildPdpUrl);
  }, [canonicalUrl]);

  const handleItemClick = () => {
    return (window.location.href = pdpUrl);
  };

  const cartItemPreviewProps: CartItemPreviewProps = {
    projectId,
    project,
    flashId,
    flashToken,
    imgUrl,
    productAttributes,
    livePreviewAttributes,
    sku,
    hammerCategory,
    isEditable,
    isDeviceSmall: isDeviceSize(DeviceSize.SMALL),
    projectVersion: schemaVersion,
    handleItemClick: handleItemClick,
    editorUrl: editorUrl
  };

  return (
    <>
      <Show when={isDeviceSize(DeviceSize.SMALL)}>
        <>
          <Show when={!showCustomOptionPrices}>
            <>
              <CartItemContent>
                <MobileImageQtyPickerGrouping>
                  <CartItemPreview {...cartItemPreviewProps} />
                  <QuantityPicker
                    isDisabled={isCartItemFetching}
                    value={toOption(qty)}
                    options={getQuantityOptionsFromState(tieredPricing)(qty)}
                    onChange={(value) => handleQtyChange(value)}
                    isCartTransparencyTestOn={showCustomOptionPrices}
                  />
                </MobileImageQtyPickerGrouping>
                <div>
                  <CartItemDetails
                    asHtml
                    title={name}
                    details={productOptions}
                    projectName={project?.name}
                    renameProjectHandler={renameProjectHandler}
                    handleItemClick={handleItemClick}
                    showCustomOptionPrices={showCustomOptionPrices}
                  />
                  <MobileMessageChip>
                    {giftWrap || giftMessage ? <StyledNotificationChip value={content} /> : null}
                  </MobileMessageChip>
                </div>
                <CartItemPrice
                  price={price}
                  qty={qty}
                  baseDiscountAmount={baseDiscountAmount}
                  isCartItemFetching={isCartItemFetching}
                  isCartTransparencyTestOn={showCustomOptionPrices}
                />
              </CartItemContent>
              <ButtonList>{actionsList}</ButtonList>
            </>
          </Show>

          <Show when={showCustomOptionPrices}>
            <CartItemContent>
              <CartItemPreview {...cartItemPreviewProps} />

              <div style={{ flex: 1 }}>
                <CartItemDetails
                  title={name}
                  details={productOptions}
                  projectName={project?.name}
                  renameProjectHandler={renameProjectHandler}
                  handleItemClick={handleItemClick}
                  showCustomOptionPrices={showCustomOptionPrices}
                />
                <DesktopQtyPriceLinksGrouping style={{ display: 'block', marginLeft: '25px' }}>
                  <DesktopQtyPrice>
                    <div>
                      <div
                        style={{
                          fontFamily: 'Crimson Text',
                          fontSize: '14px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '150%',
                          paddingBottom: '4px'
                        }}
                      >
                        Quantity
                      </div>
                      <QuantityPicker
                        isDisabled={isCartItemFetching}
                        value={toOption(qty)}
                        options={getQuantityOptionsFromState(tieredPricing)(qty)}
                        onChange={(value) => handleQtyChange(value)}
                        isCartTransparencyTestOn={showCustomOptionPrices}
                      />
                    </div>
                    <CartItemPrice
                      price={price}
                      qty={qty}
                      baseDiscountAmount={baseDiscountAmount}
                      isCartItemFetching={isCartItemFetching}
                      isCartTransparencyTestOn={showCustomOptionPrices}
                    />
                  </DesktopQtyPrice>
                </DesktopQtyPriceLinksGrouping>
              </div>
            </CartItemContent>
            <ButtonList>{actionsList}</ButtonList>
          </Show>
        </>
      </Show>
      <Show when={!isDeviceSize(DeviceSize.SMALL)}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <CartItemContainer>
            <CartItemImageDetails>
              <CartItemPreview {...cartItemPreviewProps}>
                <NotificationChipWrap>
                  {giftWrap || giftMessage ? <StyledNotificationChip value={content} /> : null}
                </NotificationChipWrap>
              </CartItemPreview>
            </CartItemImageDetails>

            <div style={{ width: '100%' }}>
              <div style={{ display: 'flex' }}>
                <CartItemDetails
                  asHtml
                  title={name}
                  details={productOptions}
                  projectName={project?.name}
                  renameProjectHandler={renameProjectHandler}
                  handleItemClick={handleItemClick}
                  showCustomOptionPrices={showCustomOptionPrices}
                />
                <Show when={!showCustomOptionPrices}>
                  <DesktopQtyPriceLinksGrouping>
                    <DesktopQtyPrice>
                      <QuantityPicker
                        isDisabled={isCartItemFetching}
                        value={toOption(qty)}
                        options={getQuantityOptionsFromState(tieredPricing)(qty)}
                        onChange={(value) => handleQtyChange(value)}
                        isCartTransparencyTestOn={showCustomOptionPrices}
                      />
                      <CartItemPrice
                        price={price}
                        qty={qty}
                        baseDiscountAmount={baseDiscountAmount}
                        isCartItemFetching={isCartItemFetching}
                        isCartTransparencyTestOn={showCustomOptionPrices}
                      />
                    </DesktopQtyPrice>
                  </DesktopQtyPriceLinksGrouping>
                </Show>
              </div>

              <Show when={showCustomOptionPrices}>
                <div>
                  <DesktopQtyPriceLinksGrouping style={{ display: 'block', marginLeft: '25px' }}>
                    <DesktopQtyPrice>
                      <div>
                        <QuantityPickerLabel>Quantity</QuantityPickerLabel>
                        <QuantityPicker
                          isDisabled={isCartItemFetching}
                          value={toOption(qty)}
                          options={getQuantityOptionsFromState(tieredPricing)(qty)}
                          onChange={(value) => handleQtyChange(value)}
                          isCartTransparencyTestOn={showCustomOptionPrices}
                        />
                      </div>
                      <CartItemPrice
                        price={price}
                        qty={qty}
                        baseDiscountAmount={baseDiscountAmount}
                        isCartItemFetching={isCartItemFetching}
                        isCartTransparencyTestOn={showCustomOptionPrices}
                      />
                    </DesktopQtyPrice>
                  </DesktopQtyPriceLinksGrouping>
                </div>
                <Show when={isGiftCard}>
                  <CartItemDetails
                    title={name}
                    details={productOptions}
                    projectName={project?.name}
                    renameProjectHandler={renameProjectHandler}
                    handleItemClick={handleItemClick}
                    showCustomOptionPrices={showCustomOptionPrices}
                    asHtml={true}
                    isGiftCard
                  />
                </Show>
              </Show>
              <ButtonList style={{ gap: showCustomOptionPrices ? '20px' : null }}>{actionsList}</ButtonList>
            </div>
          </CartItemContainer>
        </div>
      </Show>
      <Show when={showPriceDetails}>
        <CartItemOptionDetails
          options={cartItemOptionPriceDetails}
          productName={cartItemProduct.name}
          productBasePrice={isPrintSetWithPerPrintPricing(cartItemProduct) ? 0.0 : cartItemProduct.price}
          basePageCount={getBasePageCount(project, cartItemProduct, true)}
          isDeviceSmall={isDeviceSize(DeviceSize.SMALL)}
        />
      </Show>
    </>
  );
};

export default CartItem;
